<script setup lang="ts">

import {Dialog, DialogScrollContent, DialogTitle, DialogTrigger} from "@/core/components/ui/dialog";
import {Button} from "@/core/components/ui/button";
import {Label} from "@/core/components/ui/label";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/core/components/ui/select";
import {hospitalFileApi} from "@/container";
import useSwr from "@/core/composables/swr/useSwr";
import {computed, ref} from "vue";
import {Sharing} from "@/core/interfaces/Sharing";
import SharingSelect from "@/hospital/components/sharing/sharing-select/SharingSelect.vue";

const props = defineProps<{
    consumerKey: string;
    sharings: Sharing[]
}>();

const open = defineModel<boolean>("open", {
    default: false
});

const emits = defineEmits<{
    (e: 'uploaded')
}>();

const selectedFile = ref<File | null>(null);
const selectedTypeId = ref<string | undefined>(undefined);
const isUploading = ref(false);
const sharing = ref<Sharing | undefined>(props.sharings.length ? props.sharings[0] : undefined);

const {data: fileTypes, isLoading: isLoadingFileTypes} = useSwr(
    computed(() => open.value ? "hospital-file-types" : undefined),
    () => hospitalFileApi().fileTypes()
);

/**
 * handle file selection from the input element.
 */
const handleFileSelect = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    selectedFile.value = file || null;
};

/**
 * Open the file dialog programmatically.
 */
const openFileDialog = () => {
    const input = document.getElementById('file-input') as HTMLInputElement;
    input?.click();
};

/**
 * Uplaod the selected file to the server.
 */
const handleUpload = async () => {
    if (!selectedFile.value || !selectedTypeId.value || isUploading.value || !sharing.value) {
        return;
    }

    isUploading.value = true;

    try {
        await hospitalFileApi().uploadFile(sharing.value.id, {
            files: [selectedFile.value],
            type_id: Number(selectedTypeId.value),
        });

        selectedFile.value = null;
        selectedTypeId.value = undefined;
        open.value = false;
        emits('uploaded');
    } finally {
        isUploading.value = false;
    }
};

/**
 * Reset the form fields and close the dialog.
 */
const resetForm = () => {
    selectedFile.value = null;
    selectedTypeId.value = undefined;
    isUploading.value = false;
    open.value = false;
};

/**
 * Handle dialog open/close events.
 */
const handleDialogChange = (isOpen: boolean) => {
    if (!isOpen) {
        resetForm();
    }
};
</script>

<template>
  <Dialog v-model:open="open" @update:open="handleDialogChange">
    <DialogTrigger>
      <slot />
    </DialogTrigger>
    <DialogScrollContent class="flex flex-col">
      <DialogTitle>
        Ajouter un fichier
      </DialogTitle>

      <div class="space-y-4">
        <div class="space-y-2">
          <Label>Envoyer à</Label>
          <SharingSelect :sharings="sharings" :model-value="sharing" />
        </div>
        <div class="space-y-2">
          <Label for="file-type">Type de fichier</Label>
          <Select v-model="selectedTypeId" :disabled="isLoadingFileTypes">
            <SelectTrigger id="file-type">
              <SelectValue placeholder="Sélectionnez un type de fichier" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem
                v-for="fileType in fileTypes"
                :key="fileType.id"
                :value="fileType.id.toString()"
              >
                {{ fileType.name }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div class="space-y-2 flex flex-col">
          <Label for="file-input">Fichier</Label>
          <div class="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              :disabled="isUploading"
              @click="openFileDialog"
            >
              {{ selectedFile ? 'Changer le fichier' : 'Choisir un fichier' }}
            </Button>
            <p v-if="selectedFile" class="text-sm text-gray-600 truncate flex-1 min-w-0">
              {{ selectedFile.name }}
            </p>
          </div>
          <input
            id="file-input"
            type="file"
            class="hidden"
            @change="handleFileSelect"
          >
        </div>

        <div class="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            :disabled="isUploading"
            @click="resetForm"
          >
            Annuler
          </Button>
          <Button
            class="bg-primary"
            :disabled="!selectedFile || !selectedTypeId || isUploading || !sharing"
            @click="handleUpload"
          >
            {{ isUploading ? 'Téléchargement...' : 'Télécharger' }}
          </Button>
        </div>
      </div>
    </DialogScrollContent>
  </Dialog>
</template>

<style scoped>

</style>
